/**
 * =============================================================================
 * Hook: useReferralPrograms
 * Versão: 2.0.0 (Refator atomicidade — 2026-04-26)
 *
 * Descrição:
 *   CRUD dos programas de indicação (`affiliate_programs`) do tenant ativo.
 *   - Lista via RPC `get_referral_programs_with_stats` (já existente).
 *   - Mutations (create/update/delete) agora chamam RPCs SECURITY DEFINER:
 *       - upsert_referral_program  (atomicidade + validações server-side)
 *       - delete_referral_program  (Safe Delete server-side)
 *   - Toda gravação direta na tabela foi removida: as policies de WRITE
 *     foram derrubadas no banco e somente as RPCs conseguem alterar dados.
 * =============================================================================
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { Database } from '@/integrations/supabase/types';

const log = createLogger('useReferralPrograms');

export type AffiliateProgram =
  Database['public']['Tables']['affiliate_programs']['Row'];

/** Payload aceito pelas mutations (shape "amigável" do frontend). */
export interface ReferralProgramPayload {
  name: string;
  slug: string;
  description: string | null;
  program_type: 'open' | 'invite_only' | 'internal';
  default_reward_type: 'percentage' | 'fixed' | 'points' | 'tiered' | 'recurring';
  default_reward_value: number;
  cookie_duration_days: number;
  min_payout_amount: number;
  auto_approve_affiliates: boolean;
  auto_approve_commissions: boolean;
  is_active: boolean;
  // Bloco CRM
  auto_create_deal: boolean;
  crm_workspace_id: string | null;
  crm_pipeline_id: string | null;
  crm_phase_id: string | null;
  crm_source: string | null;
  crm_default_seller_id: string | null;
  crm_distribution_queue_id: string | null;
  crm_default_tags: string[];
  crm_deal_title_template: string | null;
  /**
   * v1.3.0 — Modo de atribuição (mutex vendedor x fila).
   * - 'fixed_seller': usa crm_default_seller_id (sem distribuição automática).
   * - 'queue': usa crm_distribution_queue_id (round-robin).
   * - null: sem atribuição configurada.
   */
  crm_assignment_mode?: 'fixed_seller' | 'queue' | null;
  // v1.1.0 — usados pela tela de Configurações do Programa.
  // Opcionais: quando ausentes/undefined, a RPC preserva o valor atual no banco.
  // - settings: merge raso (não substitui chaves existentes não enviadas).
  terms_html?: string | null;
  settings?: Record<string, any> | null;
  require_terms_acceptance?: boolean | null;
  /**
   * v2.1.0 — Vínculo com Modelo de Contrato (contract_templates.id) usado
   * como Termos do Programa. NULL = sem termo / usar fallback terms_html.
   */
  terms_template_id?: string | null;
}

/** Versão enriquecida com totais agregados (RPC `get_referral_programs_with_stats`). */
export type AffiliateProgramWithStats = AffiliateProgram & {
  total_affiliates: number;
  total_campaigns: number;
  total_referrals: number;
  total_earned: number;
  total_paid: number;
  /** v2.1.0 — Modelo de contrato vinculado como Termos (opcional). */
  terms_template_id?: string | null;
  terms_template_name?: string | null;
};

const QK = (tenantId?: string | null) => ['referral-programs', tenantId];

/** Lista programas do tenant ativo (com totais agregados via RPC). */
export function useReferralPrograms() {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  return useQuery<AffiliateProgramWithStats[]>({
    queryKey: QK(tenantId),
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await (supabase.rpc as any)(
        'get_referral_programs_with_stats',
        { p_tenant_hint: tenantId },
      );
      if (error) {
        log.error('Erro ao listar programas', error);
        throw error;
      }
      return ((data ?? []) as any[]).map((p) => ({
        ...p,
        total_affiliates: Number(p.total_affiliates ?? 0),
        total_campaigns: Number(p.total_campaigns ?? 0),
        total_referrals: Number(p.total_referrals ?? 0),
        total_earned: Number(p.total_earned ?? 0),
        total_paid: Number(p.total_paid ?? 0),
      })) as AffiliateProgramWithStats[];
    },
    staleTime: 30_000,
  });
}

/**
 * Converte o payload "amigável" para o shape de parâmetros p_* exigido
 * pela RPC `upsert_referral_program`.
 */
function buildUpsertParams(
  payload: ReferralProgramPayload,
  tenantId: string,
  id: string | null,
) {
  return {
    p_id: id,
    p_name: payload.name,
    p_slug: payload.slug,
    p_description: payload.description,
    p_program_type: payload.program_type,
    p_default_reward_type: payload.default_reward_type,
    p_default_reward_value: payload.default_reward_value,
    p_cookie_duration_days: payload.cookie_duration_days,
    p_min_payout_amount: payload.min_payout_amount,
    p_auto_approve_affiliates: payload.auto_approve_affiliates,
    p_auto_approve_commissions: payload.auto_approve_commissions,
    p_is_active: payload.is_active,
    p_auto_create_deal: payload.auto_create_deal,
    p_crm_workspace_id: payload.crm_workspace_id,
    p_crm_pipeline_id: payload.crm_pipeline_id,
    p_crm_phase_id: payload.crm_phase_id,
    p_crm_source: payload.crm_source,
    p_crm_default_seller_id: payload.crm_default_seller_id,
    p_crm_distribution_queue_id: payload.crm_distribution_queue_id,
    p_crm_default_tags: payload.crm_default_tags ?? [],
    p_crm_deal_title_template: payload.crm_deal_title_template,
    p_tenant_hint: tenantId,
    // v1.1.0 — campos opcionais usados pela tela de Configurações.
    p_terms_html: payload.terms_html ?? null,
    p_settings: payload.settings ?? null,
    p_require_terms_acceptance: payload.require_terms_acceptance ?? null,
    // v1.3.0 — modo de atribuição (fixed_seller | queue | null)
    p_crm_assignment_mode: payload.crm_assignment_mode ?? null,
    // v2.1.0 — Modelo de contrato vinculado como Termos do Programa
    p_terms_template_id: payload.terms_template_id ?? null,
  };
}

/** Cria/atualiza/exclui programas via RPCs atômicas. */
export function useReferralProgramMutations() {
  const qc = useQueryClient();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id ?? null;

  const invalidate = () => qc.invalidateQueries({ queryKey: QK(tenantId) });

  const createMutation = useMutation({
    mutationFn: async (payload: ReferralProgramPayload): Promise<string> => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const params = buildUpsertParams(payload, tenantId, null);
      const { data, error } = await (supabase.rpc as any)(
        'upsert_referral_program',
        params,
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async (params: { id: string; patch: ReferralProgramPayload }) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const rpcParams = buildUpsertParams(params.patch, tenantId, params.id);
      const { data, error } = await (supabase.rpc as any)(
        'upsert_referral_program',
        rpcParams,
      );
      if (error) throw error;
      return data as string;
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) throw new Error('Tenant ativo não definido');
      const { error } = await (supabase.rpc as any)('delete_referral_program', {
        p_id: id,
        p_tenant_hint: tenantId,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  return { createMutation, updateMutation, deleteMutation };
}
